import React, { useState } from "react";
import type { StartMessageFilterInput } from "../../types/start-message-types";

interface StartMessageSearchFiltersProps {
  filter: StartMessageFilterInput;
  onFilterChange: (filter: StartMessageFilterInput) => void;
  onClearFilters: () => void;
  className?: string;
}

export function StartMessageSearchFilters({
  filter,
  onFilterChange,
  onClearFilters,
  className = "",
}: StartMessageSearchFiltersProps): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState(filter.search || "");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Handle search input with debouncing
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    // Simple debouncing - update filter after user stops typing
    const timeoutId = setTimeout(() => {
      onFilterChange({
        ...filter,
        search: value.trim() || undefined,
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  // Handle filter changes
  const handleFilterChange = (
    key: keyof StartMessageFilterInput,
    value: any
  ) => {
    onFilterChange({
      ...filter,
      [key]: value,
    });
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filter).some(
    (value) => value !== undefined && value !== ""
  );

  return (
    <div className={`bg-white rounded-lg shadow border ${className}`}>
      <div className="p-4">
        {/* Search Bar */}
        <div className="flex items-center space-x-4 mb-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search start messages by name, content, or tags..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showAdvanced
                ? "bg-purple-50 text-purple-700 border-purple-200"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center">
              <svg
                className={`w-4 h-4 mr-2 transition-transform ${
                  showAdvanced ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
              Filters
            </div>
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="border-t pt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={
                    filter.isActive === undefined
                      ? ""
                      : filter.isActive.toString()
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      "isActive",
                      e.target.value === ""
                        ? undefined
                        : e.target.value === "true"
                    )
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">All Messages</option>
                  <option value="true">Active Only</option>
                  <option value="false">Inactive Only</option>
                </select>
              </div>

              {/* Conversation Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conversation
                </label>
                <input
                  type="text"
                  value={filter.conversationId || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "conversationId",
                      e.target.value || undefined
                    )
                  }
                  placeholder="Filter by conversation ID"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Tags Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  value={filter.tags?.join(", ") || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "tags",
                      e.target.value
                        ? e.target.value.split(",").map((tag) => tag.trim())
                        : undefined
                    )
                  }
                  placeholder="Filter by tags (comma-separated)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            {/* Filter Summary */}
            {hasActiveFilters && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 text-purple-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                      />
                    </svg>
                    <span className="text-sm font-medium text-purple-800">
                      Active Filters:
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {filter.search && (
                    <span className="inline-flex items-center px-2 py-1 bg-white text-purple-800 text-xs rounded border border-purple-200">
                      Search: "{filter.search}"
                    </span>
                  )}
                  {filter.isActive !== undefined && (
                    <span className="inline-flex items-center px-2 py-1 bg-white text-purple-800 text-xs rounded border border-purple-200">
                      Status: {filter.isActive ? "Active" : "Inactive"}
                    </span>
                  )}
                  {filter.conversationId && (
                    <span className="inline-flex items-center px-2 py-1 bg-white text-purple-800 text-xs rounded border border-purple-200">
                      Conversation: {filter.conversationId}
                    </span>
                  )}
                  {filter.tags && filter.tags.length > 0 && (
                    <span className="inline-flex items-center px-2 py-1 bg-white text-purple-800 text-xs rounded border border-purple-200">
                      Tags: {filter.tags.join(", ")}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
